import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Download, 
  FileSpreadsheet, 
  CheckCircle2, 
  AlertCircle,
  RefreshCw,
  Settings,
  MapPin,
  Building2,
  Users,
  PoundSterling
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const ScottishDepositExport = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [markingLodged, setMarkingLodged] = useState(false);
  
  // Data state
  const [stats, setStats] = useState(null);
  const [preview, setPreview] = useState(null);
  const [config, setConfig] = useState({ lpss_agent_id: '', configured: false });
  const [lpssAgentId, setLpssAgentId] = useState('');
  
  // Selected deposits for marking as lodged
  const [selectedDeposits, setSelectedDeposits] = useState([]);

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/scottish-deposits/stats`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/scottish-deposits/config`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setLpssAgentId(data.lpss_agent_id || '');
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  }, []);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/scottish-deposits/preview`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to load preview');
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Failed to load deposit preview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchConfig();
    fetchPreview();
  }, [fetchStats, fetchConfig, fetchPreview]);

  const handleSaveConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/scottish-deposits/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ lpss_agent_id: lpssAgentId })
      });
      
      if (response.ok) {
        toast.success('LPSS Agent ID saved successfully');
        fetchConfig();
      } else {
        toast.error('Failed to save configuration');
      }
    } catch (error) {
      toast.error('Error saving configuration');
    }
  };

  const handleDownloadExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/scottish-deposits/download`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const disposition = response.headers.get('Content-Disposition');
        const filename = disposition?.match(/filename="(.+)"/)?.[1] || 'LPSS_Bulk_Submission.xls';
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`Downloaded ${preview?.count || 0} deposits`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'No deposits to export');
      }
    } catch (error) {
      toast.error('Error downloading export');
    } finally {
      setExporting(false);
    }
  };

  const handleMarkLodged = async () => {
    if (selectedDeposits.length === 0) {
      toast.error('Please select deposits to mark as lodged');
      return;
    }
    
    setMarkingLodged(true);
    try {
      const response = await fetch(`${API_URL}/api/scottish-deposits/mark-lodged`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ deposit_ids: selectedDeposits })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedDeposits([]);
        fetchStats();
        fetchPreview();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to mark deposits');
      }
    } catch (error) {
      toast.error('Error marking deposits as lodged');
    } finally {
      setMarkingLodged(false);
    }
  };

  const toggleDepositSelection = (depositId) => {
    setSelectedDeposits(prev => 
      prev.includes(depositId) 
        ? prev.filter(id => id !== depositId)
        : [...prev, depositId]
    );
  };

  const selectAllDeposits = () => {
    if (selectedDeposits.length === preview?.deposits?.length) {
      setSelectedDeposits([]);
    } else {
      setSelectedDeposits(preview?.deposits?.map(d => d.deposit_id) || []);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  return (
    <div className="space-y-6" data-testid="scottish-deposit-export">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Scottish Deposit Export</h1>
          <p className="text-slate-600">Generate LPSS bulk deposit submission files</p>
        </div>
        <Button 
          onClick={() => { fetchStats(); fetchPreview(); }} 
          variant="outline"
          disabled={loading}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold">{stats?.pending_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Lodged</p>
                <p className="text-2xl font-bold">{stats?.lodged_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <PoundSterling className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending Value</p>
                <p className="text-2xl font-bold">{formatCurrency(stats?.pending_total || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <PoundSterling className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Lodged Value</p>
                <p className="text-2xl font-bold">{formatCurrency(stats?.lodged_total || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            LPSS Configuration
          </CardTitle>
          <CardDescription>
            Configure your LPSS Agent/Landlord ID for bulk submissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                LPSS Agent/Landlord ID
              </label>
              <Input
                value={lpssAgentId}
                onChange={(e) => setLpssAgentId(e.target.value)}
                placeholder="Enter your LPSS ID (e.g., 1234567)"
                data-testid="lpss-agent-id-input"
              />
            </div>
            <Button onClick={handleSaveConfig} className="mt-6" data-testid="save-config-btn">
              Save
            </Button>
          </div>
          {!config.configured && (
            <p className="text-sm text-amber-600 mt-2">
              ⚠️ LPSS Agent ID is required for bulk submissions
            </p>
          )}
        </CardContent>
      </Card>

      {/* Export Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Export Pending Deposits
          </CardTitle>
          <CardDescription>
            Download an Excel file formatted for LPSS bulk deposit submission
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button 
              onClick={handleDownloadExport}
              disabled={exporting || !preview?.count}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="download-export-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Generating...' : `Download LPSS Template (${preview?.count || 0} deposits)`}
            </Button>
            
            {selectedDeposits.length > 0 && (
              <Button 
                onClick={handleMarkLodged}
                disabled={markingLodged}
                variant="outline"
                className="border-green-500 text-green-700 hover:bg-green-50"
                data-testid="mark-lodged-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {markingLodged ? 'Marking...' : `Mark ${selectedDeposits.length} as Lodged`}
              </Button>
            )}
          </div>
          
          {preview?.count === 0 && (
            <p className="text-sm text-slate-500 mt-3">
              No pending Scottish deposits to export
            </p>
          )}
        </CardContent>
      </Card>

      {/* Deposit Preview Table */}
      {preview?.count > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Pending Scottish Deposits</CardTitle>
                <CardDescription>
                  {preview.count} deposits totaling {formatCurrency(preview.total_amount)}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={selectAllDeposits}>
                {selectedDeposits.length === preview.deposits.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">
                      <input
                        type="checkbox"
                        checked={selectedDeposits.length === preview.deposits.length}
                        onChange={selectAllDeposits}
                        className="rounded"
                      />
                    </th>
                    <th className="text-left py-3 px-2">Property</th>
                    <th className="text-left py-3 px-2">Postcode</th>
                    <th className="text-left py-3 px-2">Tenant</th>
                    <th className="text-left py-3 px-2">Landlord</th>
                    <th className="text-right py-3 px-2">Amount</th>
                    <th className="text-left py-3 px-2">Start Date</th>
                    <th className="text-left py-3 px-2">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.deposits.map((deposit) => (
                    <tr 
                      key={deposit.deposit_id} 
                      className={`border-b hover:bg-slate-50 ${selectedDeposits.includes(deposit.deposit_id) ? 'bg-blue-50' : ''}`}
                    >
                      <td className="py-3 px-2">
                        <input
                          type="checkbox"
                          checked={selectedDeposits.includes(deposit.deposit_id)}
                          onChange={() => toggleDepositSelection(deposit.deposit_id)}
                          className="rounded"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          <span className="font-medium">{deposit.property_address || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <Badge variant="outline" className="font-mono">
                          <MapPin className="h-3 w-3 mr-1" />
                          {deposit.postcode}
                        </Badge>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-slate-400" />
                          {deposit.tenant_name}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-slate-600">{deposit.landlord_name}</td>
                      <td className="py-3 px-2 text-right font-medium">
                        {formatCurrency(deposit.deposit_amount)}
                      </td>
                      <td className="py-3 px-2 text-slate-600">{formatDate(deposit.tenancy_start)}</td>
                      <td className="py-3 px-2 text-slate-600">{formatDate(deposit.received_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>LPSS Submission Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600">
            <li>Configure your LPSS Agent/Landlord ID above</li>
            <li>Click &quot;Download LPSS Template&quot; to generate the Excel file</li>
            <li>Review the generated file and fill in any missing Local Authority information</li>
            <li>Encrypt the file using password protection before submission</li>
            <li>Email the encrypted file to LPS Scotland</li>
            <li>After confirmation, select the deposits and click &quot;Mark as Lodged&quot;</li>
            <li>Complete the payment via bank transfer as per LPSS instructions</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScottishDepositExport;
